import { combineReducers } from 'redux'
import { handleActions, combineActions } from 'redux-actions'

import { NAV_BRAND } from '@/config'
import { PORTAL_TYPE_FPO } from '@/config/constants'
import {
  NEW_SEARCH_TRIGGER_LOCATION,
  NEW_SEARCH_TRIGGER_MANUAL,
  newSearch
} from '@/results/store/actions'
import { captureExtendedException } from '@/utils/assert'

import { map } from '../../utils/object'
import {
  JOB,
  SearchTypes,
  SORT_ORDER_DISTANCE
} from '../constants'

import * as actions from './actions'
import {
  defaultQuery,
  defaultType,
  defaultFilters,
  defaultSelectedId,
  defaultFixed,
  defaultCollapsedFilters,
  defaultUpdateUrl,
  defaultShowFirstResult,
  defaultHiddenFilters, defaultOrder,
  defaultShowMobileSearchForm,
  defaultSourceJobId,
  defaultAutoSelectItem
} from './defaultState'

export const query = handleActions(
  {
    [combineActions(actions.setSearchQuery, actions.setSearchState)]:
      (state, { payload: { query }, meta: { replace } = {} }) => {
        const baseState = replace ? defaultQuery : state
        const newState = { ...baseState, ...query }
        const { location, order } = newState
        // reset sort order when no location
        if (!location && order === SORT_ORDER_DISTANCE) {
          newState.order = defaultOrder
        }
        return newState
      },
    [newSearch.success]:
      (state, { meta: { queryParams, orderOptions } = {} }) => {
        // backend returned actual query params - update with them
        if (queryParams != null) {
          return queryParams
        }

        // backend returned possible order options - check if our is available
        if (orderOptions != null) {
          const { order = defaultOrder } = state
          if (!orderOptions.includes(order)) {
            // not in allowed stated, then fallback to first one
            const [firstAllowedOrder] = orderOptions
            return { ...state, order: firstAllowedOrder }
          }
        }

        return state
      }
  },
  defaultQuery
)

export const type = handleActions(
  {
    [combineActions(actions.setSearchType, actions.setSearchState)]:
      (state, { payload: { type }, meta: { replace } = {} }) => type != null ? type : (replace ? defaultType : state)
  },
  defaultType
)

export const showFirstResult = handleActions(
  {
    [combineActions(...actions.SearchTypeActions.map(actions => actions.setSelectedId))]:
      (state, { payload: selectedId } = {}) => selectedId != null,
    [actions.showFirstResult]: (state, { payload: { show } }) => show,
    [actions.navigateUp]: () => false
  },
  defaultShowFirstResult
)

export const showMobileSearchForm = handleActions(
  { [actions.showMobileSearchForm]: () => true },
  defaultShowMobileSearchForm
)

export const filters = handleActions(
  {
    [actions.resetSearchFilters]: () => defaultFilters,
    [combineActions(actions.setSearchState, actions.setSearchFilters)]:
      (state, { payload: { filters }, meta: { replace } = {} }) => ({ ...(replace ? defaultFilters : state), ...filters })
  },
  defaultFilters
)

const selectionReducer = (setSelectedAction, defaultValue, getValue) => handleActions({
  [newSearch.request]: (current, { meta: { trigger } }) => {
    switch (trigger) {
      case NEW_SEARCH_TRIGGER_LOCATION:
        return current
      case NEW_SEARCH_TRIGGER_MANUAL:
        return defaultValue
      default:
        captureExtendedException(
          new Error('Unhandled new search trigger in selection reducer'),
          { tags: { trigger } }
        )
        if (process.env.NODE_ENV !== 'production') {
          throw new Error(`Unhandled new search trigger ${trigger} in selection reducer`)
        }
        return current
    }
  },
  [actions.navigateUp]: () => defaultValue,
  [setSelectedAction]: (_, action) => getValue(defaultValue, action)
}, defaultValue)

export const resultsReducer = (setSelectedAction, searchType) => combineReducers(
  {
    selectedId: selectionReducer(
      setSelectedAction,
      defaultSelectedId,
      (defaultValue, { payload = defaultValue }) => payload
    ),
    ...(searchType === JOB && {
      sourceJobId: selectionReducer(
        setSelectedAction,
        defaultSourceJobId,
        (defaultValue, { meta: { sourceJobId = defaultValue } = {} }) => sourceJobId
      )
    })
  }
)

export const results = combineReducers(map(SearchTypes,
  (value, searchType) => resultsReducer(actions[searchType].setSelectedId, searchType)
))

export const fixed = handleActions({}, defaultFixed)

export const collapsedFilters = handleActions({
  [actions.toggleFilters]: state => !state,
  [newSearch.request]: state => NAV_BRAND.portal_type === PORTAL_TYPE_FPO || state,
  [combineActions(actions.setSearchQuery, actions.setSearchFilters, actions.setSearchState, actions.resetSearchFilters)]: () => defaultCollapsedFilters
}, defaultCollapsedFilters)

export const updateUrl = handleActions({
  [combineActions(actions.setSearchQuery, actions.setSearchType, actions.resetSearchFilters, actions.setSearchState, actions.setSearchFilters)]: () => true,
  [combineActions(...actions.SearchTypeActions.map(actions => actions.setSelectedId))]: (state, { meta: { updateUrl = state } }) => updateUrl,
  [actions.navigateUp]: (state, { meta: { updateUrl = state } }) => updateUrl
}, defaultUpdateUrl)

export const hiddenFilters = handleActions({
  [actions.hideFilters]: (_, { payload }) => payload,
  [newSearch.request]: () => NAV_BRAND.portal_type === PORTAL_TYPE_FPO
}, defaultHiddenFilters)

export const autoSelectItem = handleActions({
  [combineActions(...actions.SearchTypeActions.map(actions => actions.setSelectedId))]:
    (state, { payload, meta: { autoSelectItem = false } }) => autoSelectItem
}, defaultAutoSelectItem)

export default combineReducers({
  query,
  type,
  filters,
  showFirstResult,
  showMobileSearchForm,
  results,
  fixed,
  collapsedFilters,
  updateUrl,
  hiddenFilters,
  autoSelectItem
})
